import { useState } from 'react'
import { Link } from 'react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { t, type Key } from '../../i18n'
import { Button, Icon, Note } from '../../ui'
import { nodePath } from './api'
import { balanceLines, balanceQueryKey, groupErrorText, groupsApi, type BalanceKind } from './groups'
import { utcClock } from './operatorEvents'

/*
 * The balance journal of a node group (GROUP-UI-2; GROUP-3 API, node-group.md §6.4):
 * ConsoleEvents rows (.sx-feed > .sx-ev) — UTC time with seconds (11 Time), a state
 * icon or `user` for an operator's action, the subject in 500 and one sentence with
 * numbers (NG-03 A: one member, one row, the reason). Newest on top; Pause stops
 * loading new entries; a filter by kind and "Show more" (the API's `before` cursor).
 * Eight choices are more than four segments, so the filter is the compact select of
 * the filter presets (B6A): .sx-select.sm labelled «Kind», data-set when not «All».
 *
 * DS: NG-10 (decided 01.11) — a day line (.sx-label, muted, UTC, day first: «6 Oct
 * 2026», «6 oct 2026», «6 окт. 2026») above the first row and at every change of day.
 * The dropdown is FilterPresets' (system 1793550620-8d2b); the day line and the Kind
 * filter are not in the ConsoleEvents card yet — the designer adds them.
 */

const KINDS: ('' | BalanceKind)[] = ['', 'weights', 'ramp_step', 'cap', 'member_state', 'offload', 'outputs_moved', 'hub_fallback']

const day = (iso: string) => iso.slice(0, 10)
/** Day first in every language, no year word: «6 Oct 2026», «6 oct 2026», «6 окт. 2026» (UTC). */
export function dayLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const parts = new Intl.DateTimeFormat(t('group.log.locale'), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).formatToParts(d)
  const get = (k: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === k)?.value ?? ''
  return `${get('day')} ${get('month')} ${get('year')}`
}

export function GroupJournal({ id, group, nameOf }: { id: string; group: string; nameOf: (id?: string) => string }) {
  const [kind, setKind] = useState<'' | BalanceKind>('')
  const [paused, setPaused] = useState(false)
  const q = useInfiniteQuery({
    queryKey: balanceQueryKey(id, kind),
    queryFn: ({ pageParam }) => groupsApi.balance(id, kind, pageParam || undefined),
    initialPageParam: 0,
    getNextPageParam: (p) => p.next_before,
    refetchInterval: paused ? false : 15_000,
    retry: false,
  })
  const entries = q.data?.pages.flatMap((p) => p.items) ?? []
  const lines = entries.flatMap((e) => balanceLines(e, nameOf, group))
  return (
    <section className="sx-panel !p-0" aria-labelledby="group-log-title" data-testid="group-journal">
      <div className="sx-toolbar">
        <span className="sx-label" id="group-log-title">{t('group.log.title')}</span>
        <span style={{ flex: 1 }} />
        {/* FilterPresets dropdown: .sx-select.sm, label `muted` + value, data-set when a kind is chosen */}
        <label className="sx-select sm min-w-[200px] focus-within:border-accent focus-within:shadow-focus max-md:h-[44px]" data-set={kind ? 'true' : undefined} data-testid="group-log-kind-box">
          <span className="k text-muted">{t('group.log.kind')}</span>
          <select id="group-log-kind" className="min-w-0 flex-1 cursor-pointer appearance-none border-0 bg-transparent text-[12px] text-text outline-none" value={kind}
            onChange={(e) => setKind(e.target.value as '' | BalanceKind)} data-testid="group-log-kind">
            {KINDS.map((k) => (
              <option key={k || 'all'} value={k}>{t(`group.log.k.${k || 'all'}` as Key)}</option>
            ))}
          </select>
          <Icon name="chevron-down" size={16} className="pointer-events-none text-muted" />
        </label>
        <Button size="sm" variant="ghost" className="max-md:min-h-[44px]" aria-pressed={paused} data-testid="group-log-pause"
          onClick={() => { setPaused(!paused); if (paused) void q.refetch() }}>
          {t(paused ? 'group.log.resume' : 'group.log.pause')}
        </Button>
      </div>
      {paused && <p className="border-b border-line px-3 py-1.5 text-[12px] text-muted" role="status" data-testid="group-log-paused">{t('group.log.paused')}</p>}
      {q.error ? (
        <div className="p-3"><Note state="warn" data-testid="group-log-error">{groupErrorText(q.error)}</Note></div>
      ) : q.isPending ? (
        <p className="px-3 py-3 text-[13px] text-muted">…</p>
      ) : lines.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-muted" data-testid="group-log-empty">{t(kind ? 'group.log.emptyKind' : 'group.log.empty')}</p>
      ) : (
        <div className="sx-feed" data-testid="group-log-rows">
          {lines.map((l, i) => {
            const newDay = i === 0 || day(lines[i - 1]!.at) !== day(l.at)
            return (
              <div key={l.key} style={{ display: 'contents' }}>
                {newDay && (
                  <div className="sx-label border-b border-line px-3 py-1.5 text-muted" data-testid="group-log-day">{dayLabel(l.at)}</div>
                )}
                <div className="sx-ev" data-state={l.state} data-testid={`group-log-${l.key}`} title={`${l.at.replace('T', ' ').slice(0, 19)} UTC`}>
                  <span className="tm">{utcClock(l.at)}</span>
                  <Icon name={l.icon} size={16} />
                  <span>
                    {l.node ? (
                      <Link to={nodePath(l.node)} className="hover:underline"><b className="sx-mono">{l.subject}</b></Link>
                    ) : (
                      <b className="sx-mono">{l.subject}</b>
                    )}{' '}
                    <span className="d"><Nums text={l.text} /></span>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {q.hasNextPage && (
        <div className="flex justify-center border-t border-line p-2">
          <Button size="sm" variant="ghost" className="max-md:min-h-[44px]" busy={q.isFetchingNextPage} data-testid="group-log-more" onClick={() => void q.fetchNextPage()}>
            {t('group.log.more')}
          </Button>
        </div>
      )}
    </section>
  )
}

/** Numbers of a sentence in mono, tabular (README rule 8). */
function Nums({ text }: { text: string }) {
  const parts = text.split(/(\d+(?:[.,]\d+)?(?: %)?)/)
  return (
    <>
      {parts.map((p, i) => (i % 2 ? <span key={i} className="sx-mono">{p}</span> : p))}
    </>
  )
}
